import { useEffect, useState } from "react";
import {
	Navigate,
	useLocation,
	useNavigate,
	useParams
} from "react-router-dom";
import Page from "src/layouts/Page";
import { ConfirmationModal } from "src/modals/ConfirmationModal/ConfirmationModal";
import HashtagForm, {
	HashtagFormValues
} from "../../components/HashtagForm/HashtagForm";
import useAdminHashtag from "../../hooks/useAdminHashtag";
import useEditHashtag from "../../hooks/useEditHashtag";
import useDeleteHashtag from "../../hooks/useDeleteHashtag";
import { adaptEditHashtagPayload } from "../../adapters/adaptHashtagPayload";
import { formValuesToHashtag } from "../../adapters/formValuesToHashtag";

function hashtagToFormValues(hashtag: AdminHashtag): HashtagFormValues {
	return {
		name: hashtag.name,
		description: hashtag.description ?? "",
		color: hashtag.color,
		isPrivate: hashtag.isPrivate,
		isFavourite: hashtag.isFavourite,
		excludeFromMainFeed: hashtag.excludeFromMainFeed,
		image: null
	};
}

const HASHTAG_LIST = "/social-feed/hashtags";

const EditHashtag = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const { hashtagId } = useParams<{ hashtagId: string }>();
	const uuid = hashtagId ?? "";
	const UUID_RE =
		/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
	const isValidId = UUID_RE.test(uuid);

	const [showDeleteModal, setShowDeleteModal] = useState(false);
	const [defaultValues, setDefaultValues] = useState<HashtagFormValues>({
		name: "",
		description: "",
		color: "#2563eb",
		isPrivate: false,
		isFavourite: false,
		excludeFromMainFeed: false,
		image: null
	});

	const { data: hashtag } = useAdminHashtag(isValidId ? uuid : "");
	const { mutate: editHashtag, isLoading: isEditing } = useEditHashtag();
	const { mutate: deleteHashtag, isLoading: isDeleting } = useDeleteHashtag();

	useEffect(() => {
		if (hashtag) {
			setDefaultValues(hashtagToFormValues(hashtag));
		}
	}, [hashtag]);

	const goBack = () => {
		// If user navigated here from within the app, go back; otherwise fall back to hashtag list
		if (location.key !== "default") {
			navigate(-1);
		} else {
			navigate(HASHTAG_LIST);
		}
	};

	if (!isValidId) {
		return <Navigate to={HASHTAG_LIST} replace />;
	}

	const handleSubmit = (data: HashtagFormValues, imageRemoved: boolean) => {
		const payload = adaptEditHashtagPayload(
			formValuesToHashtag(data),
			data.image,
			imageRemoved
		);

		editHashtag(
			{ uuid, payload },
			{
				onSuccess: goBack
			}
		);
	};

	const handleDelete = () => {
		deleteHashtag(uuid, {
			onSuccess: () => {
				setShowDeleteModal(false);
				goBack();
			}
		});
	};

	return (
		<Page>
			<HashtagForm
				defaultValues={defaultValues}
				onSubmit={handleSubmit}
				isSubmitting={isEditing}
				isEdit
				onDelete={() => setShowDeleteModal(true)}
				existingImageReference={hashtag?.image ?? null}
				currentName={hashtag?.name}
			/>

			<ConfirmationModal
				isOpen={showDeleteModal}
				title="Delete hashtag?"
				description="This action cannot be undone."
				onCancel={() => {
					if (!isDeleting) setShowDeleteModal(false);
				}}
				cancelBtnProps={{ disabled: isDeleting }}
				onSubmit={handleDelete}
				submitBtnProps={{ isLoading: isDeleting }}
			/>
		</Page>
	);
};

export default EditHashtag;
